/**
 * LOHP event factories — each returns a `{_type, ...data}` object for `tracker.publishEvent()`.
 *
 * `createClickEvent` from @udemy-v2/event-tracking is re-exported for generic link clicks.
 */

// --- Discovery Events (Enriched Skills, Trending Skills, Cert Prep, PSU, Discovery Units) ---

interface DiscoveryTrackingData {
    id: number;
    type: string;
    trackingId: string;
    serveTrackingId: string;
    backendSource: string;
    position: number;
    uiRegion: string;
    relatedSourceId?: string | null;
    relatedSourceType?: string | null;
}

export function createDiscoveryItemClickEvent(data: DiscoveryTrackingData) {
    return {_type: 'DiscoveryItemClickEvent' as const, ...data};
}

export function createDiscoveryImpressionEvent(data: DiscoveryTrackingData) {
    return {_type: 'DiscoveryItemImpressionEvent' as const, ...data};
}

/** Shared fields for Trending Skills topic / top-skill discovery events. */
export function createTrendingSkillsDiscoveryTrackingData(
    topic: {id: number},
    position: number,
): DiscoveryTrackingData {
    return {
        id: topic.id,
        type: 'course_topic',
        trackingId: String(topic.id),
        serveTrackingId: '',
        backendSource: 'discovery',
        position,
        uiRegion: 'trending_skills',
    };
}

// --- GWG Events ---

interface AcademySelectedData {
    academySlug: string;
    uiRegion: string;
}

export function createAcademySelectedEvent(data: AcademySelectedData) {
    return {_type: 'AcademySelected' as const, ...data};
}

export function createAcademyPresentedEvent(data: {academySlug: string; uiRegion: string}) {
    return {_type: 'AcademyPresented' as const, ...data};
}

// --- Generic Click Events ---

export {createClickEvent} from '@udemy-v2/event-tracking';

// --- Testimonials ---

/**
 * Maps a CMS testimonial `labelText` (schema type) to the event `type` value,
 * mirroring v1's `getTestimonialEventTypeFromSchemaType` (react-discovery-units).
 * Returns '' for unknown/missing labels.
 */
const TESTIMONIAL_EVENT_TYPE: Record<string, string> = {
    CASE_STUDY: 'case-study',
    COURSE_REVIEW: 'course-review',
    THIRD_PARTY: 'third-party',
};

export function getTestimonialEventType(labelText: string | null | undefined): string {
    return labelText ? TESTIMONIAL_EVENT_TYPE[labelText] ?? '' : '';
}

export const TESTIMONIAL_CASE_STUDY_LABEL = 'CASE_STUDY';

interface TestimonialEventData {
    id: string;
    type: string;
    uiRegion: string;
}

interface TestimonialClickData extends TestimonialEventData {
    url: string;
}

export function createTestimonialClickEvent(data: TestimonialClickData) {
    return {_type: 'TestimonialClickEvent' as const, ...data};
}

export function createTestimonialImpressionEvent(data: TestimonialEventData) {
    return {_type: 'TestimonialImpressionEvent' as const, ...data};
}

// --- UFB Notice Events (fired only for CaseStudy testimonials, mirroring v1) ---

interface UFBNoticeData {
    url: string;
    placement: string;
    locale: string;
}

export function createUFBNoticeClickEvent(data: UFBNoticeData) {
    return {_type: 'UFBNoticeClickEvent' as const, variant: null, ...data};
}

export function createUFBNoticeImpressionEvent(data: UFBNoticeData) {
    return {_type: 'UFBNoticeImpressionEvent' as const, ...data};
}
